import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  type ChatModelOption,
  type ChatToolCategory,
  type ChatAccountAssociationStatus,
} from '../shared/api-client';
import { useFeatureFlags } from '../feature-flags/feature-flag-hooks';
import {
  CHAT_TOOL_CATEGORY_UI_ORDER,
  DEFAULT_ON_UI_CATEGORIES,
  chatToolCategoryFeatureFlag,
  chatToolCategoryUiLabel,
  chatToolCategoryUiDescription,
  isKnownToolCategory,
  type ChatToolCategory as ChatToolCategoryName,
} from './chat-tool-category-ui-registry';
import { initThreadStream } from './chat-stream-registry';
import {
  uploadPendingFiles,
  ACCEPTED_TYPES,
  MAX_FILE_SIZE,
  type PendingAttachment,
} from './chat-attachment-upload';
import { ChatAccountSelectorComponent, type ChatAccountSelectorValue } from './ChatAccountSelectorComponent';
import { ChatAttachmentTrayComponent } from './ChatAttachmentTrayComponent';
import { ChatAttachmentPickerComponent } from './ChatAttachmentPickerComponent';
import './ChatSpineComposerComponent.css';

/** The spine composer's text box height, dragged or keyed by the user and remembered per browser. */
const INPUT_HEIGHT_KEY = 'chat.spineInputHeight';
const INPUT_HEIGHT_MIN = 120;
const INPUT_HEIGHT_MAX = 600;
const INPUT_HEIGHT_DEFAULT = 144;
const INPUT_HEIGHT_KEY_STEP = 40;

function clampInputHeight(h: number): number {
  return Math.max(INPUT_HEIGHT_MIN, Math.min(INPUT_HEIGHT_MAX, Math.round(h)));
}

function readStoredInputHeight(): number {
  try {
    const stored = Number(localStorage.getItem(INPUT_HEIGHT_KEY));
    return stored ? clampInputHeight(stored) : INPUT_HEIGHT_DEFAULT;
  } catch {
    return INPUT_HEIGHT_DEFAULT; // storage blocked (private window): the default is fine
  }
}

function storeInputHeight(h: number): void {
  try {
    localStorage.setItem(INPUT_HEIGHT_KEY, String(h));
  } catch {
    // storage blocked: the height just isn't remembered
  }
}

/**
 * The ambient tool category — always on, never shown in the picker, always
 * added to a Custom allowlist. Mirrors `AMBIENT_TOOL_CATEGORY` in ChatPage so a
 * spine-started thread has the same tool surface as a welcome-started one.
 */
const AMBIENT_TOOL_CATEGORY = 'Files';

/**
 * A new-thread composer for the dashboard spine (frames 3b/4a/5a).
 *
 * Unlike `ChatPage`'s composer this one only ever starts a brand-new thread —
 * there is no existing thread to append to from the dashboard. It POSTs to
 * `/api/v1/chat/message` directly (the same endpoint `DefaultChatTransport`
 * uses), and on the 202 it registers the stream with `chat-stream-registry`
 * and navigates to `/apps/chat/:threadId`. `ChatPage` then re-attaches to that
 * registered stream and renders the generating turn — so this component owns
 * *no* streaming UI, only "compose + kick off + hand off".
 *
 * The send body is built to match ChatPage's transport `body()` for a new
 * thread: account + privacy + model fields are sent only because there is no
 * `threadId` yet, and Auto Tools omits `enabledCategories` (backend applies no
 * restriction) while Custom sends the explicit allowlist plus the ambient
 * `Files` category.
 */
export function ChatSpineComposerComponent(): JSX.Element {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();

  const [input, setInput] = useState('');
  const [inputHeight, setInputHeight] = useState<number>(readStoredInputHeight);

  // Drag the grip below the box to make it taller or shorter. Pointer capture keeps the
  // drag going when the cursor leaves the grip, for mouse, pen and touch alike.
  function handleResizePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    e.preventDefault();
    const grip = e.currentTarget;
    grip.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startHeight = inputHeight;
    let current = startHeight;
    const onMove = (ev: PointerEvent): void => {
      current = clampInputHeight(startHeight + (ev.clientY - startY));
      setInputHeight(current);
    };
    const onEnd = (): void => {
      storeInputHeight(current);
      grip.removeEventListener('pointermove', onMove);
      grip.removeEventListener('pointerup', onEnd);
      grip.removeEventListener('pointercancel', onEnd);
    };
    grip.addEventListener('pointermove', onMove);
    grip.addEventListener('pointerup', onEnd);
    grip.addEventListener('pointercancel', onEnd);
  }

  function handleResizeKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    const next =
      e.key === 'ArrowDown' ? inputHeight + INPUT_HEIGHT_KEY_STEP
      : e.key === 'ArrowUp' ? inputHeight - INPUT_HEIGHT_KEY_STEP
      : e.key === 'End' ? INPUT_HEIGHT_MAX
      : e.key === 'Home' ? INPUT_HEIGHT_MIN
      : null;
    if (next === null) return;
    e.preventDefault();
    const h = clampInputHeight(next);
    setInputHeight(h);
    storeInputHeight(h);
  }
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [accountSelector, setAccountSelector] = useState<ChatAccountSelectorValue>({
    accountId: null,
    status: 'unknown',
  });
  const [availableModels, setAvailableModels] = useState<ChatModelOption[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [availableCategories, setAvailableCategories] = useState<ChatToolCategory[]>([]);
  const [accountAssociationRequired, setAccountAssociationRequired] = useState(false);
  const [autoTools, setAutoTools] = useState(true);
  const [enabledCategories, setEnabledCategories] = useState<Set<string>>(
    new Set(DEFAULT_ON_UI_CATEGORIES),
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  // Drag state. `dragNear` is true while a file drag is anywhere over the
  // browser window (faint wash on the composer); `dragActive` tightens to the
  // input box itself (full overlay + highlight). Mirrors ChatPage's pattern.
  const [dragNear, setDragNear] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const inputBoxDepthRef = useRef(0);

  // Refs that the send path reads after state has moved on — same pattern as
  // ChatPage's `*Ref` mirrors of composer state.
  const accountSelectorRef = useRef(accountSelector);
  accountSelectorRef.current = accountSelector;
  const autoToolsRef = useRef(autoTools);
  autoToolsRef.current = autoTools;
  const enabledCategoriesRef = useRef(enabledCategories);
  enabledCategoriesRef.current = enabledCategories;
  const pendingAttachmentsRef = useRef(pendingAttachments);
  pendingAttachmentsRef.current = pendingAttachments;
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    void api.getEnabledChatModels().then((result) => {
      if (result.success) {
        setAvailableModels(result.data);
        const def = result.data.find((m) => m.isDefault) ?? result.data[0];
        if (def) setSelectedModelId(def.id);
      }
    });
    void api.getChatToolCategories().then((result) => {
      if (result.success) setAvailableCategories(result.data);
    });
    void api.getChatPolicy().then((result) => {
      if (result.success) setAccountAssociationRequired(result.data.accountAssociationRequired);
    });
  }, []);

  // Window-level drag awareness: the moment a file drag enters the browser
  // window at all, light up `dragNear` so the composer gets a faint wash and the
  // user knows a drop will do something. Cleared when the drag leaves the
  // window or is dropped anywhere. Accepts OS file drags AND VFS path drags
  // (a file being dragged out of the embedded File Library).
  useEffect(() => {
    const acceptsDrag = (e: DragEvent): boolean => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      const arr = Array.from(types);
      return (
        arr.includes('Files') ||
        arr.includes('text/vfs-paths') ||
        arr.includes('text/vfs-path')
      );
    };

    const onWindowDragEnter = (e: DragEvent): void => {
      if (!acceptsDrag(e)) return;
      dragDepthRef.current += 1;
      setDragNear(true);
    };
    const onWindowDragOver = (e: DragEvent): void => {
      if (!acceptsDrag(e)) return;
      // preventDefault so a drop is permitted somewhere in the window.
      e.preventDefault();
    };
    const onWindowDragLeave = (e: DragEvent): void => {
      if (!acceptsDrag(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragNear(false);
    };
    const onWindowDrop = (e: DragEvent): void => {
      if (!acceptsDrag(e)) return;
      dragDepthRef.current = 0;
      setDragNear(false);
      setDragActive(false);
      inputBoxDepthRef.current = 0;
      // Don't preventDefault here for OS files dropped outside the composer —
      // let the browser do its thing. The composer's own onDrop handles drops
      // that land on it and stops propagation there.
    };

    window.addEventListener('dragenter', onWindowDragEnter);
    window.addEventListener('dragover', onWindowDragOver);
    window.addEventListener('dragleave', onWindowDragLeave);
    window.addEventListener('drop', onWindowDrop);
    return () => {
      window.removeEventListener('dragenter', onWindowDragEnter);
      window.removeEventListener('dragover', onWindowDragOver);
      window.removeEventListener('dragleave', onWindowDragLeave);
      window.removeEventListener('drop', onWindowDrop);
    };
  }, []);

  // Same visibility rule as ChatPage's `visibleToolCategories`: available,
  // flag-enabled, not the ambient category. No persona allowlist here — the
  // spine composer is free-form, not persona-scoped.
  const visibleToolCategories = useMemo(
    () =>
      availableCategories.filter((c) => {
        if (!c.available) return false;
        const flag = chatToolCategoryFeatureFlag(c.category);
        if (flag && !isEnabled(flag)) return false;
        if (c.category === AMBIENT_TOOL_CATEGORY) return false;
        return true;
      }),
    [availableCategories, isEnabled],
  );

  const isSending = sending;
  const hasUploadingAttachments = pendingAttachments.some((a) => a.status === 'uploading');
  const sendDisabled =
    isSending ||
    !input.trim() ||
    hasUploadingAttachments;

  function toggleCategory(category: string): void {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  async function handleSend(): Promise<void> {
    const text = inputRef.current.trim();
    if (!text || isSending) return;
    if (hasUploadingAttachments) return;
    // Hard gate at first message for required-role users — same as ChatPage.
    if (accountAssociationRequired && accountSelectorRef.current.status === 'unknown') {
      setError('Pick a customer account or "No Client" before sending.');
      return;
    }

    const attachmentIds = pendingAttachmentsRef.current
      .filter((a) => a.status === 'ready' && a.attachmentId)
      .map((a) => a.attachmentId!);

    const sel = availableModels.find((m) => m.id === selectedModelId) ?? null;
    const toolFields = autoToolsRef.current
      ? {}
      : {
          enabledCategories: [...new Set([...enabledCategoriesRef.current, AMBIENT_TOOL_CATEGORY])],
        };

    // The schema (`ChatMessageSendSchema`) requires either a personaTaskId or
    // at least one message. Free-form send → one user message carrying the text.
    const body = {
      messages: [{ role: 'user' as const, content: text }],
      threadId: null,
      attachmentIds,
      accountId: accountSelectorRef.current.accountId,
      accountAssociationStatus: accountSelectorRef.current.status,
      isPrivate: false,
      isCollaborative: false,
      ...(sel ? { provider: sel.provider, model: sel.modelId } : {}),
      ...toolFields,
    };

    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/chat/message', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        setError('Your session has expired. Refresh the page and sign in again.');
        setSending(false);
        return;
      }
      if (res.status === 403) {
        setError('You cannot post to this chat.');
        setSending(false);
        return;
      }
      if (res.status === 409) {
        // A brand-new thread should never 409 (no prior turn), but if the id
        // space collided the message is recoverable — keep the text.
        setError('A response is already being generated for that chat.');
        setSending(false);
        return;
      }
      if (!res.ok) {
        // Surface the server's reason when it sent one (matches the chat-page
        // transport). The generic status line alone is useless for debugging.
        let reason = `Server error (${res.status}). Please try again.`;
        try {
          const body = await res.clone().json();
          const serverMsg = body?.error?.message as string | undefined;
          const serverDetail = body?.error?.details?.error as string | undefined;
          const got = serverDetail ?? serverMsg;
          if (got) reason = `${reason}\n${got}`;
        } catch { /* not JSON — keep the generic reason */ }
        setError(reason);
        setSending(false);
        return;
      }
      const json = (await res.json()) as { data?: { threadId?: string; startCursor?: string } };
      const threadId = json?.data?.threadId;
      const startCursor = json?.data?.startCursor;
      if (!threadId) {
        setError('No thread id returned. Please try again.');
        setSending(false);
        return;
      }
      // Register the stream so ChatPage re-attaches on navigation and the
      // generating turn renders there. Without this the turn would be invisible
      // until the user manually reopened the thread.
      if (startCursor !== undefined) initThreadStream(threadId, startCursor);

      // Hand off: clear composer state and jump to the live thread.
      setInput('');
      setPendingAttachments([]);
      navigate(`/apps/chat/${threadId}`);
    } catch {
      setError('Failed to reach the server. Please try again.');
      setSending(false);
    }
  }

  return (
    <div className={`chat-spine-composer${dragNear ? ' drag-near' : ''}`}>
      <div className="chat-spine-composer-account">
        <ChatAccountSelectorComponent
          value={accountSelector}
          onChange={setAccountSelector}
          required={accountAssociationRequired}
        />
      </div>
      {availableModels.length > 0 && (
        <div className="chat-spine-composer-model">
          <select
            className="chat-spine-composer-model-select"
            value={selectedModelId}
            onChange={(e) => setSelectedModelId(e.target.value)}
            aria-label="Model"
            disabled={isSending}
          >
            {availableModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName} ({m.provider}){m.isDefault ? ' • default' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <ChatAttachmentTrayComponent
        attachments={pendingAttachments}
        onAttachmentsChange={setPendingAttachments}
      />
      <div
        className={`chat-spine-composer-input-box${dragActive ? ' drag-active' : ''}`}
        onDragEnter={(e) => {
          if (
            e.dataTransfer.types.includes('Files') ||
            e.dataTransfer.types.includes('text/vfs-paths') ||
            e.dataTransfer.types.includes('text/vfs-path')
          ) {
            e.preventDefault();
            inputBoxDepthRef.current += 1;
            setDragActive(true);
          }
        }}
        onDragOver={(e) => {
          // Accept both OS file drops (upload) and VFS path drops (a reference
          // dragged from the embedded File Library). Without this the browser
          // would only allow the OS-file drop and would bounce the library drag.
          if (
            e.dataTransfer.types.includes('Files') ||
            e.dataTransfer.types.includes('text/vfs-paths') ||
            e.dataTransfer.types.includes('text/vfs-path')
          ) {
            e.preventDefault();
            // 'copy' (not 'link'): the source sets effectAllowed to 'copy' or
            // 'copyMove', and the spec only permits dropEffect values that the
            // source allowed. 'link' is not among them, so a 'link' dropEffect
            // would make the browser refuse the drop and the path never reaches
            // onDrop. 'copy' is permitted by both 'copy' and 'copyMove'.
            e.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          inputBoxDepthRef.current = Math.max(0, inputBoxDepthRef.current - 1);
          if (inputBoxDepthRef.current === 0) setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          inputBoxDepthRef.current = 0;
          setDragActive(false);
          setDragNear(false);
          dragDepthRef.current = 0;
          if (isSending || hasUploadingAttachments) return;
          // A VFS path dragged from the File Library: append the path(s) to the
          // composer as a reference the model reads with readFile — same shape as
          // the chat drawer's "Use in chat". Not an upload: copying would fork
          // the file and re-create the ambiguity deleting `promote` removed.
          const vfsPaths =
            e.dataTransfer.getData('text/vfs-paths') || e.dataTransfer.getData('text/vfs-path');
          if (vfsPaths) {
            const paths = vfsPaths.split('\n').filter(Boolean);
            if (paths.length > 0) {
              setInput((prev) =>
                prev ? `${prev} ${paths.join(' ')}` : paths.join(' '),
              );
            }
            return;
          }
          const files = e.dataTransfer.files;
          if (files && files.length > 0) {
            void uploadPendingFiles(files, pendingAttachmentsRef.current, setPendingAttachments);
          }
        }}
      >
        <textarea
          className="chat-spine-composer-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!sendDisabled) void handleSend();
            }
          }}
          placeholder="Ask anything…"
          rows={5}
          style={{ height: inputHeight }}
          disabled={isSending}
          aria-label="Message"
        />
        {dragActive && (
          <div className="chat-spine-composer-drop-overlay">
            <span className="chat-spine-composer-drop-overlay-icon" aria-hidden="true">⬇</span>
            <span className="chat-spine-composer-drop-overlay-text">Drop files here to attach</span>
          </div>
        )}
      </div>
      <div
        className="chat-spine-composer-resize"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Message box height. Drag, or use the up and down arrow keys."
        aria-valuemin={INPUT_HEIGHT_MIN}
        aria-valuemax={INPUT_HEIGHT_MAX}
        aria-valuenow={inputHeight}
        tabIndex={0}
        title="Drag to make the message box taller or shorter"
        onPointerDown={handleResizePointerDown}
        onKeyDown={handleResizeKeyDown}
      />
      <div className="chat-spine-composer-controls">
        <ChatAttachmentPickerComponent
          attachments={pendingAttachments}
          onAttachmentsChange={setPendingAttachments}
          disabled={isSending || hasUploadingAttachments}
        />
        {visibleToolCategories.length > 0 && (
          <button
            type="button"
            className={`chat-spine-composer-tool-toggle${autoTools ? '' : ' custom'}`}
            onClick={() => setToolPickerOpen((v) => !v)}
            title={autoTools ? 'Auto Tools — the assistant decides. Turn off to pick tools manually.' : 'Custom tools — only the categories you select are available.'}
          >
            <span className="chat-spine-composer-tool-toggle-switch" aria-hidden="true" />
            Tools{' '}
            <span className="chat-spine-composer-tool-toggle-mode">
              {autoTools ? `Auto · ${visibleToolCategories.length + 1}` : 'Custom'}
            </span>
          </button>
        )}
        <button
          type="button"
          className="chat-spine-composer-send"
          onClick={() => void handleSend()}
          disabled={sendDisabled}
          aria-label="Send"
        >
          {isSending ? '…' : '↑'}
        </button>
      </div>
      {toolPickerOpen && visibleToolCategories.length > 0 && (
        <div className="chat-spine-composer-tool-picker">
          <div className="chat-spine-composer-tool-picker-head">
            <span className="chat-spine-composer-tool-picker-eyebrow">Tool categories</span>
            <button
              type="button"
              className={`chat-spine-composer-tool-picker-mode${autoTools ? ' active' : ''}`}
              onClick={() => setAutoTools(true)}
            >
              Auto
            </button>
            <button
              type="button"
              className={`chat-spine-composer-tool-picker-mode${!autoTools ? ' active' : ''}`}
              onClick={() => setAutoTools(false)}
            >
              Custom
            </button>
          </div>
          <div className="chat-spine-composer-tool-picker-grid">
            {CHAT_TOOL_CATEGORY_UI_ORDER.filter((c) =>
              visibleToolCategories.some((v) => v.category === c),
            ).map((c) => {
              const known = isKnownToolCategory(c);
              const label = known ? chatToolCategoryUiLabel(c) : c;
              const desc = known ? chatToolCategoryUiDescription(c) : undefined;
              const checked = enabledCategories.has(c);
              return (
                <label key={c} className="chat-spine-composer-tool-picker-row" title={desc}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCategory(c)}
                    disabled={autoTools}
                  />
                  <span className="chat-spine-composer-tool-picker-row-label">{label}</span>
                </label>
              );
            })}
          </div>
          <p className="chat-spine-composer-tool-picker-foot">
            Files is always on — attachments stay readable without picking it.
          </p>
        </div>
      )}
      {error && <div className="chat-spine-composer-error">{error}</div>}
    </div>
  );
}

// Re-export so the dashboard can surface the same limits as the chat page if
// it ever needs to validate pre-upload.
export { ACCEPTED_TYPES, MAX_FILE_SIZE, AMBIENT_TOOL_CATEGORY };
export type { PendingAttachment };
export type { ChatAccountAssociationStatus };
export type { ChatToolCategoryName };
